import type { ReactNode } from 'react';
import type { DeskSnapshot, SavedView } from '../api/generated/types.gen';
import { zone } from '../format';

const FEED_NAMES: Record<string, string> = { coinbase: 'Coinbase', kraken: 'Kraken', kalshi: 'Kalshi' };
const CONNECTION: Record<string, string> = { connecting: 'Connecting…', reconnecting: 'Reconnecting…', offline: 'Offline', stale: 'Updates stalled', waiting: 'Waiting for data', backgrounded: 'Paused in background', incompatible: 'Reload required' };

export function TopBar({ desk, connection, replay, views, now, onLive, onFreeze, onView, onSources, account }: {
  desk: DeskSnapshot | null; connection: string; replay: string | null; views: SavedView[]; now: number;
  onLive: () => void; onFreeze: () => void; onView: (id: string) => void; onSources: () => void; account: ReactNode;
}) {
  return <header className="topbar">
    <a href="/desk/BTC/hourly" className="brand" aria-label="Crypto Thunder desk">
      <svg width="16" height="20" viewBox="0 0 20 26" aria-hidden="true"><path d="M12 0 1 15h7l-2 11 13-16h-7z" fill="var(--amber)"/></svg>
      <span className="wordmark">Crypto Thunder</span>
    </a>
    <div className="feeds" aria-label="Data sources">
      {desk?.feeds.filter(f => f.source !== 'projector').map(f => <span key={f.source} className="feed" title={f.detail}>
        <i className={`dot ${f.status}`}/>{FEED_NAMES[f.source] ?? f.source}{f.age_seconds != null && f.status !== 'unavailable' ? ` ${f.age_seconds < 10 ? f.age_seconds.toFixed(1) : Math.round(f.age_seconds)}s` : ` ${f.status}`}
      </span>)}
      {/* Plan 057: the settlement index, recorded from Kalshi's live connection. */}
      {desk?.benchmark ? <span className="feed" data-testid="index-feed" title={`CF Benchmarks ${desk.benchmark.index_id} ${desk.benchmark.value}, the price contracts settle on`}>
        <i className={`dot ${desk.benchmark.status}`}/>CF index {desk.benchmark.age_seconds < 10 ? desk.benchmark.age_seconds.toFixed(1) : Math.round(desk.benchmark.age_seconds)}s</span>
        : <span className="feed" data-testid="index-feed" title={`No CF Benchmarks ${desk?.settlement_index ?? 'index'} tick recorded yet`}><i className="dot unavailable"/>CF index off</span>}
    </div>
    {account}
    {connection !== 'connected' && connection !== 'replay' && <span className="connection" role="status">{CONNECTION[connection] ?? connection}</span>}
    <button className="tool" onClick={onSources} disabled={!desk}>Sources &amp; rules</button>
    <div className="mode">
      <button aria-pressed={!replay} onClick={onLive}>Live</button>
      <button aria-pressed={!!replay} onClick={onFreeze} disabled={!desk}>Freeze</button>
      {replay && <select aria-label="Recorded view" value={replay} onChange={e => onView(e.target.value)}>
        {!views.some(v => v.view_id === replay) && <option value={replay}>Frozen view</option>}
        {views.map(v => <option key={v.view_id} value={v.view_id}>{new Date(v.recorded_at).toLocaleTimeString()} · #{v.revision}</option>)}
      </select>}
    </div>
    <span className="clock">{new Date(now * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })} {zone()}</span>
  </header>;
}
